import React from "react";
import { IoArrowForward } from "react-icons/io5";
import { LuStar } from "react-icons/lu";

const FALLBACK_AVATARS = [
  "https://i.pinimg.com/1200x/ad/c7/ae/adc7ae6697026c600409215d7e63f1d0.jpg",
  "https://i.pinimg.com/736x/33/6c/97/336c9766a02b5e2158b64ce2d76b2ccc.jpg",
  "https://i.pinimg.com/736x/10/c9/eb/10c9ebf1467fa3b02e2ca3b5e03128c8.jpg",
];

function getTitle(gathering) {
  return String(
    gathering?.name ||
      gathering?.title ||
      gathering?.eventName ||
      "Your first gathering"
  ).trim();
}

function normalizeTitle(title) {
  return String(title)
    .replace(/\s+/g, " ")
    .trim();
}

function renderTitleWithBracketWeight(title) {
  const parts = normalizeTitle(title).split(
    /(\[[^\]]+\])/g
  );

  return parts.map((part, index) => {
    const isBracket =
      /^\[[^\]]+\]$/.test(part);

    return isBracket ? (
      <span
        key={index}
        style={styles.titleBracket}
      >
        {part}
      </span>
    ) : (
      <React.Fragment key={index}>
        {part}
      </React.Fragment>
    );
  });
}

function getStartDate(gathering) {
  const eventDate = gathering?.eventDate;
  const startTime = gathering?.startTime12;

  if (!eventDate) {
    return startTime
      ? String(startTime).replace(/^0/, "")
      : "Date coming soon";
  }

  try {
    const [year, month, day] = eventDate
      .split("-")
      .map(Number);

    const date = new Date(
      year,
      month - 1,
      day
    );

    const dateLabel =
      date.toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
      });

    return startTime
      ? `${dateLabel} · ${String(
          startTime
        ).replace(/^0/, "")}`
      : dateLabel;
  } catch {
    return startTime
      ? `${eventDate} · ${String(
          startTime
        ).replace(/^0/, "")}`
      : eventDate;
  }
}

function getCapacity(gathering) {
  const capacity = Number(
    gathering?.displayCapacity ??
      gathering?.capacity ??
      gathering?.maxAttendees ??
      0
  );

  return Number.isFinite(capacity)
    ? capacity
    : 0;
}

function getAttendeeCount(gathering) {
  const count = Number(
    gathering?.displayAttendeeCount ??
      gathering?.attendeeCount ??
      gathering?.reservedCount ??
      gathering?.attendees?.length ??
      0
  );

  return Number.isFinite(count)
    ? count
    : 0;
}

function getAttendees(gathering) {
  return Array.isArray(gathering?.attendees)
    ? gathering.attendees
    : [];
}

function getAvatar(attendee, index) {
  if (!attendee) {
    return FALLBACK_AVATARS[
      index % FALLBACK_AVATARS.length
    ];
  }

  const person =
    attendee?.user ||
    attendee?.member ||
    attendee;

  const raw =
    person?.profilePicUrl ||
    person?.profilePictureUrl ||
    person?.imageUrl ||
    person?.avatarUrl ||
    person?.photoUrl ||
    person?.avatar ||
    "";

  if (!raw) {
    return FALLBACK_AVATARS[
      index % FALLBACK_AVATARS.length
    ];
  }

  return raw.startsWith("http")
    ? raw
    : `${process.env.REACT_APP_API_URL}${raw}`;
}


export default function FirstGatheringRecommendation({
  gathering,
  firstName = "",
  loading = false,
  onViewGathering,
  onViewAll,
}) {
  if (loading) {
    return (
      <div style={styles.shell}>
        <div style={styles.pageHeader}>
          <div style={styles.loadingEyebrow} />
          <div style={styles.loadingHeading} />
          <div style={styles.loadingSubheading} />
        </div>

        <div style={styles.loadingCard}>
          <div style={styles.loadingHost} />
          <div style={styles.loadingTitle} />
          <div style={styles.loadingMeta} />
          <div style={styles.loadingAvatars} />
        </div>
      </div>
    );
  }

  if (!gathering) {
    return (
      <div style={styles.shell}>
        <div style={styles.pageHeader}>
          <h1 style={styles.pageTitle}>
            Find one you’ll love
          </h1>

          <p style={styles.pageSubtitle}>
            Explore upcoming gatherings and
            choose one that feels right.
          </p>
        </div>

        <button
          type="button"
          onClick={onViewAll}
          style={styles.primaryButton}
        >
          <span>See all gatherings</span>
          <IoArrowForward size={19} />
        </button>
      </div>
    );
  }

  const title = normalizeTitle(
  getTitle(gathering)
);

  const dateLabel =
    getStartDate(gathering);


  const capacity =
    getCapacity(gathering);

  const attendeeCount =
    getAttendeeCount(gathering);

  const spotsRemaining =
    capacity > 0
      ? Math.max(
          capacity - attendeeCount,
          0
        )
      : null;

  const attendees =
    getAttendees(gathering);

  const description = String(
    gathering?.shortDescription ||
      gathering?.description ||
      ""
  ).trim();

  return (
    <div style={styles.shell}>
      <div style={styles.pageHeader}>

        <h1 style={styles.pageTitle}>
        PICKED FOR YOU
        </h1>

      <p style={styles.pageSubtitle}>
    {firstName
      ? `${firstName}, we picked this gathering for you.`
      : "We picked this gathering for you."}
  </p>
</div>

      <div style={styles.card}>
        <div style={styles.cardContent}>
      <div style={styles.forYouBadge}>
  <LuStar size={13} strokeWidth={2.2} />
  <span>Picked for you</span>
</div>

<h2 style={styles.eventTitle}>
  {renderTitleWithBracketWeight(title)}
</h2>

          <div style={styles.eventMeta}>
            <span>{dateLabel}</span>
          </div>

          <div style={styles.avatarRow}>
            {[0, 1, 2].map((index) => (
              <div
                key={index}
                style={{
                  ...styles.avatar,
                  marginLeft:
                    index === 0
                      ? 0
                      : -13,
                  zIndex: 3 - index,
                  backgroundImage: `url(${getAvatar(
                    attendees[index],
                    index
                  )})`,
                }}
              />
            ))}
          </div>

          <div style={styles.capacityCopy}>
            {spotsRemaining !== null ? (
              <>
                <div
                  style={
                    styles.capacityStrong
                  }
                >
                  {spotsRemaining} of{" "}
                  {capacity}
                </div>

                <div
                  style={
                    styles.capacityLight
                  }
                >
                  spots remaining
                </div>
              </>
            ) : (
              <>
                <div
                  style={
                    styles.capacityStrong
                  }
                >
                  {attendeeCount}+
                </div>

                <div
                  style={
                    styles.capacityLight
                  }
                >
                  people going
                </div>
              </>
            )}
          </div>

          {description && (
            <p style={styles.description}>
              {description}
            </p>
          )}
        </div>
      </div>

      <button
        type="button"
        onClick={onViewGathering}
        style={styles.primaryButton}
      >
        <span>View & Reserve</span>
        <IoArrowForward size={19} />
      </button>

      <button
        type="button"
        onClick={onViewAll}
        style={styles.secondaryButton}
      >
        See all gatherings
      </button>
    </div>
  );
}

const styles = {
  shell: {
    width: "100%",
    maxWidth: 430,
    margin: "0 auto",
    textAlign: "center",
    fontFamily:
      "'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },

  pageHeader: {
    width: "100%",
    marginBottom: 20,
  },


  pageTitle: {
      fontSize: '1.6rem',
  lineHeight: 1,
  fontWeight: 900,
  color: '#0b0b0b',
  margin: '0px 0 10px',
  letterSpacing: '-0.045em',
  textTransform: 'uppercase',
  },

  pageSubtitle: {
    maxWidth: 330,
    margin: "12px auto 0",
    color: "rgba(17,17,17,0.52)",
    fontSize: 14,
    lineHeight: 1.42,
    fontWeight: 520,
    letterSpacing: "-0.02em",
  },

  card: {
  position: "relative",
  width: "100%",
  minHeight: 390,
  padding: "0px 26px 20px",
    boxSizing: "border-box",
    background: "#fff",
    border:
      ".5px solid rgba(17,17,17,0.09)",
    borderRadius: 12,

    display: "flex",
    flexDirection: "column",
  },

  hostRow: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    alignSelf: "center",
    gap: 9,
  },

  hostMark: {
    width: 27,
    height: 27,
    borderRadius: "50%",
    background: "#111",
    color: "#fff",
    display: "grid",
    placeItems: "center",
    fontSize: 8.5,
    lineHeight: 1,
    fontWeight: 900,
    letterSpacing: "-0.06em",
    textTransform: "lowercase",
  },

  hostText: {
    color: "rgba(17,17,17,0.48)",
    fontSize: 12.5,
    lineHeight: 1,
    fontWeight: 520,
    letterSpacing: "-0.02em",
  },

  hostStrong: {
    color: "#111",
    fontWeight: 800,
  },
cardContent: {
  flex: 1,
  width: "100%",
  padding: "10px 8px 4px",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
},

eventTitle: {
  maxWidth: 350,
  margin: 0,
  color: "#000",
  fontFamily:
    "'Recoleta Alt', Georgia, 'Times New Roman', serif",
  fontSize:
    "clamp(1.5rem, 7vw, 2.5rem)",
  lineHeight: 1.12,
  fontWeight: 700,
  textAlign: "center",
},

eventMeta: {
  width: "100%",
  marginTop: 18,
  color: "#000",
  fontSize: 14,
  lineHeight: 1.25,
  fontWeight: 650,
  letterSpacing: "-0.022em",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 4,
  whiteSpace: "nowrap",
},


avatarRow: {
  marginTop: 32,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
},

avatar: {
  width: 38,
  height: 38,
  borderRadius: "50%",
  backgroundColor: "#ececec",
  backgroundSize: "cover",
  backgroundPosition: "center",
  border: "1px solid #fff",
  boxSizing: "border-box",
},

capacityCopy: {
  marginTop: 13,
  textAlign: "center",
},

capacityStrong: {
  color: "#111",
  fontSize: 13.5,
  lineHeight: 1,
  fontWeight: 850,
  letterSpacing: "-0.025em",
},

capacityLight: {
  marginTop: 5,
  color: "rgba(17,17,17,0.56)",
  fontSize: 12.5,
  lineHeight: 1,
  fontWeight: 620,
  letterSpacing: "-0.018em",
},

description: {
  maxWidth: 310,
  margin: "20px auto 0",
  color: "rgba(17,17,17,0.64)",
  fontSize: 13.5,
  lineHeight: 1.45,
  fontWeight: 500,
  letterSpacing: "-0.018em",
  textAlign: "center",
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  textOverflow: "ellipsis",
},
  primaryButton: {
    width: "100%",
    minHeight: 54,
    marginTop: 20,
    padding: "14px 20px",
    border: "none",
    borderRadius: 12,
    background: "#111",
    color: "#fff",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 9,
    fontSize: 14,
    lineHeight: 1,
    fontWeight: 760,
    letterSpacing: "-0.025em",
    cursor: "pointer",
    boxSizing: "border-box",
    WebkitTapHighlightColor:
      "transparent",
  },

  secondaryButton: {
    marginTop: 10,
    padding: "11px 14px",
    border: "none",
    background: "transparent",
    color: "rgba(17,17,17,0.52)",
    fontSize: 13,
    lineHeight: 1,
    fontWeight: 700,
    letterSpacing: "-0.02em",
    cursor: "pointer",
  },

  loadingCard: {
    width: "100%",
    minHeight: 410,
    padding: 28,
    boxSizing: "border-box",
    border:
      "1px solid rgba(17,17,17,0.08)",
    borderRadius: 22,
    background: "#fff",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },

  loadingEyebrow: {
    width: 120,
    height: 10,
    margin: "0 auto 14px",
    borderRadius: 999,
    background: "#ededed",
  },

  loadingHeading: {
    width: "72%",
    height: 34,
    margin: "0 auto",
    borderRadius: 999,
    background: "#ececec",
  },

  loadingSubheading: {
    width: "55%",
    height: 14,
    margin: "14px auto 0",
    borderRadius: 999,
    background: "#f1f1f1",
  },

  loadingHost: {
    width: 110,
    height: 27,
    borderRadius: 999,
    background: "#f1f1f1",
  },

  loadingTitle: {
    width: "78%",
    height: 34,
    marginTop: 40,
    borderRadius: 999,
    background: "#ececec",
  },

  loadingMeta: {
    width: "65%",
    height: 14,
    marginTop: 24,
    borderRadius: 999,
    background: "#f1f1f1",
  },

  loadingAvatars: {
    width: 100,
    height: 38,
    marginTop: 32,
    borderRadius: 999,
    background: "#f0f0f0",
  },
  titleBracket: {
  color: "rgba(17,17,17,0.72)",
  fontWeight: 500,
  letterSpacing: "-0.055em",
},

forYouBadge: {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 6,
  marginBottom: 18,
  padding: "8px 11px",
  borderRadius: 999,
  background: "#FFF6CC",
  color: "#000",
  fontSize: 11,
  lineHeight: 1,
  fontWeight: 800,
  letterSpacing: "-0.01em",
},
};